import type { QueryClient } from '@tanstack/react-query';
import type { CampaignsCampaignModel, CampaignsListCampaignsResponse } from '@customerio/hydra-ui-api';
import { actionKeys, parseAction, type Action } from '../action/entity';
import { parseSubscriptionTopic, subscriptionTopicKeys, type SubscriptionTopic } from '../subscription-topic/entity';
import { parseTag, tagKeys, type Tag } from '../tag/entity';
import { campaignKeys, parseCampaign, type Campaign } from './entity';

/**
 * Cluster propagation helpers.
 *
 * `populateCampaignModel` is the cluster-shape sideload helper used by:
 *  - `getCampaign(id)`'s queryFn (read-side propagation)
 *  - every CampaignModel-returning mutation's `onSuccess`
 *    (`addCampaignActions`, `deleteCampaignActions`, `createSplitTest`,
 *    `selectSplitWinner`)
 *
 * `populateCampaignsList` handles the LIST response shape — multiple
 * campaigns with sibling-array sideloads grouped at the top level. The
 * actions array gets regrouped by `campaign_id` so each campaign's
 * relation slot reflects the full set returned for that campaign.
 */

export interface ParsedCampaignModel {
  campaign: Campaign;
  actions: readonly Action[];
  tags: readonly Tag[];
  subscription_topics: readonly SubscriptionTopic[];
}

/**
 * The generated CampaignModel-shaped responses (`PUT /campaigns/:id` and
 * `GET /campaigns/:id`) type `campaign` as optional and the sideload
 * arrays as non-null, but the wire omits whichever clusters the campaign
 * hasn't pulled in (e.g. an action-less draft has no `actions`). Widen
 * the arrays back to optional so the `?? []` guards are honest, and
 * require an explicit `campaign` null-check at parse time.
 */
type GeneratedCampaignModel = Omit<CampaignsCampaignModel, 'actions' | 'tags' | 'subscription_topics'> & {
  actions?: CampaignsCampaignModel['actions'];
  tags?: CampaignsCampaignModel['tags'];
  subscription_topics?: CampaignsCampaignModel['subscription_topics'];
};

export function parseCampaignModel(model: GeneratedCampaignModel): ParsedCampaignModel {
  if (model.campaign == null) {
    throw new Error('missing campaign in campaign model response');
  }
  return {
    campaign: parseCampaign(model.campaign),
    actions: (model.actions ?? []).map(parseAction),
    tags: (model.tags ?? []).map(parseTag),
    subscription_topics: (model.subscription_topics ?? []).map((topic) =>
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      parseSubscriptionTopic(topic as Parameters<typeof parseSubscriptionTopic>[0]),
    ),
  };
}

/**
 * Fan a CampaignModel envelope across the cache. Single source of truth
 * for "I just got a CampaignModel-shaped response, propagate it correctly."
 *
 * **Normalized.** Each entity is written exactly once — to its own detail
 * cache slot. Relation views (e.g. `campaign.actions`) are NOT stored as
 * separate cache slots; they're derived at read time by following FKs
 * through `useEntitiesByIds(detailSlotForId, fkArray)`. Same data
 * appears in only one place; updates propagate everywhere automatically.
 *
 * What this does write:
 *   - `campaign.keys.detail(wsId, campaignId)` — the campaign itself.
 *   - `action.keys.detail(wsId, actionId)` — each action.
 *   - `tag.keys.detail(wsId, tagId)` — each tag.
 *   - `subscription_topic.keys.detail(wsId, topicId)` — each topic.
 *
 * What relation reads do:
 *   - `useCampaignActions(campaign)` reads `campaign.workflow_action_ids`
 *     and resolves each id via `useEntitiesByIds(...)`.
 *   - `useCampaignTags(campaign)` reads `campaign.tag_ids` similarly.
 *   - `useCampaignsForTag(tagId)` derives via `useCampaignsCached({ select })`.
 *   - `useCampaignForAction(action)` follows `action.campaign_id`.
 *
 * Trade-off: there's no addressable cache slot for "this campaign's
 * actions" specifically — invalidations work at the entity-detail level
 * instead. Mutations that update an action setQueryData its detail slot;
 * every relation view that references it re-renders through TanStack
 * Query's per-query notification. Net less plumbing, fewer slots to
 * keep in sync.
 */
export function populateCampaignModel(qc: QueryClient, workspaceId: number, model: ParsedCampaignModel): void {
  qc.setQueryData(campaignKeys.detail(workspaceId, model.campaign.id), model.campaign);
  // No relation slots — relation views (campaign.actions, campaign.tags,
  // campaign.subscription_topics) are derived at read time by following
  // FK arrays into the detail caches. See `useCampaignActions(campaign)`
  // etc. in `campaign/queries.ts`.
  populateDetailSlots(qc, model.actions, (a) => actionKeys.detail(workspaceId, a.id));
  populateDetailSlots(qc, model.tags, (t) => tagKeys.detail(workspaceId, t.id));
  populateDetailSlots(qc, model.subscription_topics, (st) => subscriptionTopicKeys.detail(workspaceId, st.id));
}

/* ------------------------------------------------------------------------- */
/* List response                                                              */
/* ------------------------------------------------------------------------- */

export interface ParsedCampaignsListResponse {
  campaigns: readonly Campaign[];
  actions: readonly Action[];
  tags: readonly Tag[];
  subscription_topics: readonly SubscriptionTopic[];
  meta?: CampaignsListCampaignsResponse['meta'];
}

/**
 * The generated list response types the sideload arrays as non-null, but
 * the wire omits whichever the request excluded (`excludeActions`,
 * `excludeTags`, …). Widen them to optional so the `?? []` guards below
 * are honest — same treatment as {@link GeneratedCampaignModel}.
 */
type GeneratedCampaignsListResponse = Omit<
  CampaignsListCampaignsResponse,
  'actions' | 'tags' | 'subscription_topics'
> & {
  actions?: CampaignsListCampaignsResponse['actions'];
  tags?: CampaignsListCampaignsResponse['tags'];
  subscription_topics?: CampaignsListCampaignsResponse['subscription_topics'];
};

export function parseCampaignsListResponse(raw: GeneratedCampaignsListResponse): ParsedCampaignsListResponse {
  return {
    campaigns: raw.campaigns.map(parseCampaign),
    actions: (raw.actions ?? []).map(parseAction),
    tags: (raw.tags ?? []).map(parseTag),
    subscription_topics: (raw.subscription_topics ?? []).map((topic) =>
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      parseSubscriptionTopic(topic as Parameters<typeof parseSubscriptionTopic>[0]),
    ),
    meta: raw.meta,
  };
}

/**
 * Cluster propagation for the LIST response. Writes detail caches only;
 * relation views are derived at read time from the FK arrays each
 * campaign carries (`workflow_action_ids`, `tag_ids`, `subscription_topic_id`).
 */
export function populateCampaignsList(
  qc: QueryClient,
  workspaceId: number,
  response: ParsedCampaignsListResponse,
): void {
  populateDetailSlots(qc, response.campaigns, (c) => campaignKeys.detail(workspaceId, c.id));
  populateDetailSlots(qc, response.actions, (a) => actionKeys.detail(workspaceId, a.id));
  populateDetailSlots(qc, response.tags, (t) => tagKeys.detail(workspaceId, t.id));
  populateDetailSlots(qc, response.subscription_topics, (st) => subscriptionTopicKeys.detail(workspaceId, st.id));
}

/**
 * Last-write-wins cache write for each sideloaded entity.
 *
 * Deliberately does **not** `cancelQueries` first the way the optimistic
 * mutations do. The list response is the freshest server snapshot for
 * every entity it contains — if a concurrent detail fetch is in flight,
 * letting it land later is fine (it would only carry equally fresh or
 * fresher data, since both queries hit the same backend). The race the
 * optimistic-delete cancel guards against is *client-state vs. server*,
 * which doesn't apply here.
 */
function populateDetailSlots<T>(qc: QueryClient, items: readonly T[], keyFor: (item: T) => readonly unknown[]): void {
  for (const item of items) qc.setQueryData(keyFor(item), item);
}
